import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, BookOpen, ChevronDown, LayoutDashboard, LogOut, MessageSquare, Monitor, Moon, Network,
  PanelLeftClose, PanelLeftOpen, Receipt, ShieldCheck, Sparkles, Sun, Users,
  type LucideIcon,
} from 'lucide-react';

import { useAuth } from '@app/hooks/useAuth';
import { useTheme, type ThemeChoice } from '@app/hooks/useTheme';
import { useDocContext } from '@app/hooks/useDocContext';

interface NavLeaf {
  to: string;
  label: string;
  end?: boolean; // exact match only (e.g. "All docs" vs its categories)
}

// Main menus carry the icons; sub-items are plain text under their group.
// A group with `to` and no items is a direct link.
interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: string;
  items?: NavLeaf[];
}

const NAV: NavGroup[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, to: '/' },
  {
    key: 'customers', label: 'Customers', icon: Users,
    items: [
      { to: '/subscribers', label: 'Subscribers' },
      { to: '/enquiries', label: 'Enquiries' },
      { to: '/us-texting', label: 'US & Canada texting' },
      { to: '/contacts', label: 'Contacts' },
      { to: '/api-keys', label: 'API Keys' },
    ],
  },
  {
    key: 'messaging', label: 'Messaging', icon: MessageSquare,
    items: [
      { to: '/messages', label: 'Messages' },
      { to: '/campaigns', label: 'Campaigns' },
      { to: '/templates', label: 'Templates' },
      { to: '/delivery', label: 'Delivery' },
    ],
  },
  {
    key: 'billing', label: 'Billing', icon: Receipt,
    items: [
      { to: '/plans', label: 'Plans' },
      { to: '/invoices', label: 'Invoices' },
      { to: '/transactions', label: 'Transactions' },
    ],
  },
  { key: 'resellers', label: 'Resellers', icon: Network, to: '/resellers' },
  {
    key: 'services', label: 'Services', icon: Sparkles,
    items: [
      { to: '/hlr', label: 'Number Lookup (HLR)' },
      { to: '/otp', label: 'OTP' },
      { to: '/ai-usage', label: 'AI Usage' },
    ],
  },
  {
    key: 'compliance', label: 'Compliance', icon: ShieldCheck,
    items: [
      { to: '/compliance', label: 'Consents & templates' },
      { to: '/audit', label: 'Audit Log' },
    ],
  },
  { key: 'system', label: 'System Health', icon: Activity, to: '/system' },
  {
    key: 'docs', label: 'Docs', icon: BookOpen,
    items: [
      { to: '/docs', label: 'All docs', end: true },
      { to: '/docs/c/getting-started', label: 'Getting started' },
      { to: '/docs/c/admin-guide', label: 'Admin guide' },
      { to: '/docs/c/operations', label: 'Operations runbooks' },
      { to: '/docs/c/development', label: 'Development' },
      { to: '/docs/c/decisions', label: 'Decisions & changelog' },
    ],
  },
];

function isActivePath(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

function groupOfPath(pathname: string): string | undefined {
  return NAV.find((g) => (g.to && isActivePath(pathname, g.to))
    || g.items?.some((i) => isActivePath(pathname, i.to)))?.key;
}

const THEMES: { value: ThemeChoice; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

const COLLAPSE_KEY = 'talky-admin-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function nextTheme(c: ThemeChoice): ThemeChoice {
  return c === 'light' ? 'dark' : c === 'dark' ? 'system' : 'light';
}

export function Layout() {
  const { user, logout } = useAuth();
  const { choice, setChoice } = useTheme();
  // On a doc page, its category's sub-item counts as the current page
  const docCategory = useDocContext((s) => s.category);
  const navigate = useNavigate();
  const location = useLocation();
  // Mobile drawer: closed by default. Open ⇒ slide-in over content with backdrop.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop only: icon rail instead of the full sidebar; remembered per browser
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Expanded main menus; the one holding the current page is always open
  const activeGroup = useMemo(() => groupOfPath(location.pathname), [location.pathname]);
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );
  // Auto-close on route change so picking a nav item dismisses the drawer.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Mobile drawer behaves like a dialog: focus moves in, Tab stays inside,
  // Escape closes it, the page behind doesn't scroll, focus returns after
  const asideRef = useRef<HTMLElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!drawerOpen) {
      if (wasOpen.current) openButtonRef.current?.focus();
      wasOpen.current = false;
      return undefined;
    }
    wasOpen.current = true;
    const aside = asideRef.current;
    const focusables = () => {
      const all = [...(aside?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])];
      const shown = all.filter((el) => el.offsetParent !== null);
      return shown.length ? shown : all; // (no layout info, e.g. in tests)
    };
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setDrawerOpen(false); return; }
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);
  useEffect(() => {
    if (activeGroup) setOpenGroups((s) => (s.has(activeGroup) ? s : new Set(s).add(activeGroup)));
  }, [activeGroup]);

  const toggleGroup = (key: string) => {
    setOpenGroups((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* private mode */ }
      return !c;
    });
  };
  const CurrentThemeIcon = THEMES.find((t) => t.value === choice)?.icon ?? Monitor;
  // The rail only applies on lg+; the mobile drawer always shows labels
  const rail = collapsed ? 'lg:hidden' : '';

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile top bar (hidden on lg+ where the persistent sidebar is visible). */}
      <div className="fixed left-0 right-0 top-0 z-30 flex items-center justify-between border-b border-slate-800 bg-slate-900 px-3 py-2 text-slate-100 lg:hidden">
        <button
          ref={openButtonRef}
          type="button"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          className="rounded p-1.5 hover:bg-slate-800"
        >
          <PanelLeftOpen size={20} aria-hidden />
        </button>
        <div className="flex items-center gap-2">
          <img src="/talky-logo.png" alt="Talky" width={127} height={96} className="h-7 w-auto" />
          <span className="text-sm font-semibold">Admin</span>
        </div>
        <button
          type="button"
          aria-label={`Theme: ${choice}. Switch to ${nextTheme(choice)}`}
          title={`Theme: ${choice}`}
          onClick={() => setChoice(nextTheme(choice))}
          className="rounded p-1.5 hover:bg-slate-800"
        >
          <CurrentThemeIcon size={18} aria-hidden />
        </button>
      </div>

      {/* Backdrop (mobile only, when drawer open). */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        />
      )}

      {/* Sidebar:
          - Desktop (lg+): sticky column, full (w-64) or icon rail (w-16).
          - Mobile (<lg): off-screen drawer that slides in when drawerOpen. */}
      <aside
        ref={asideRef}
        aria-label="Main navigation"
        {...(drawerOpen ? { role: 'dialog', 'aria-modal': true } : {})}
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-800 bg-slate-900 text-slate-100 transition-[transform,width] duration-200',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:sticky lg:top-0 lg:h-screen lg:translate-x-0',
          collapsed ? 'lg:w-16' : 'lg:w-64',
        ].join(' ')}
      >
        <div className={`flex items-center border-b border-slate-800 py-4 ${collapsed ? 'justify-between px-4 lg:justify-center lg:px-2' : 'justify-between px-4'}`}>
          <div className={rail}>
            <img src="/talky-logo.png" alt="Talky" width={127} height={96} className="h-10 w-auto" />
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-300/70">Admin Panel</div>
          </div>
          <button
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleCollapsed}
            className="hidden rounded p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white lg:block"
          >
            {collapsed ? <PanelLeftOpen size={18} aria-hidden /> : <PanelLeftClose size={18} aria-hidden />}
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-slate-300 hover:bg-slate-800 lg:hidden"
          >
            <PanelLeftClose size={18} aria-hidden />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV.map(({ key, label, icon: Icon, to, items }) => {
            const groupActive = activeGroup === key;
            const mainClass = [
              'mb-0.5 flex w-full items-center gap-3 rounded px-3 py-3 text-sm font-medium transition-colors lg:py-2',
              collapsed ? 'lg:justify-center lg:px-0' : '',
            ].join(' ');
            if (to && !items) {
              return (
                <NavLink
                  key={key}
                  to={to}
                  end={to === '/'}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) => [
                    mainClass,
                    isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  ].join(' ')}
                >
                  <Icon size={18} className="shrink-0" aria-hidden />
                  <span className={collapsed ? 'lg:sr-only' : ''}>{label}</span>
                </NavLink>
              );
            }
            const open = openGroups.has(key);
            return (
              <div key={key} className="mb-0.5">
                <button
                  type="button"
                  title={collapsed ? label : undefined}
                  aria-expanded={open}
                  onClick={() => {
                    // On the icon rail, a main menu opens the sidebar on that group
                    if (collapsed && window.matchMedia('(min-width: 1024px)').matches) {
                      toggleCollapsed();
                      setOpenGroups((s) => new Set(s).add(key));
                    } else {
                      toggleGroup(key);
                    }
                  }}
                  className={[
                    mainClass,
                    groupActive ? 'text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                    groupActive && collapsed ? 'lg:bg-blue-600' : '',
                  ].join(' ')}
                >
                  <Icon size={18} className="shrink-0" aria-hidden />
                  <span className={`flex-1 text-left ${collapsed ? 'lg:sr-only' : ''}`}>{label}</span>
                  <ChevronDown
                    size={16}
                    aria-hidden
                    className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${collapsed ? 'lg:hidden' : ''}`}
                  />
                </button>
                {open && (
                  <div className={`mb-1 ml-5 border-l border-slate-700 pl-2 ${rail}`}>
                    {items!.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) => [
                          'block rounded px-3 py-3 text-sm transition-colors lg:py-1.5',
                          isActive || (docCategory !== null && item.to === `/docs/c/${docCategory}`)
                            ? 'bg-blue-600 text-white' : 'text-slate-300/80 hover:bg-slate-800 hover:text-white',
                        ].join(' ')}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`border-t border-slate-800 p-3 text-xs ${collapsed ? 'lg:px-2' : ''}`}>
          {/* Theme: segmented control, or one cycling button on the rail */}
          <div
            role="radiogroup"
            aria-label="Theme"
            className={`mb-3 grid grid-cols-3 gap-1 rounded-md bg-slate-800/70 p-1 ${rail}`}
          >
            {THEMES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={choice === value}
                onClick={() => setChoice(value)}
                className={[
                  'flex min-h-[44px] items-center justify-center gap-1 rounded px-1.5 transition-colors lg:min-h-0 lg:py-1',
                  choice === value ? 'bg-slate-600 text-white' : 'text-slate-300 hover:text-white',
                ].join(' ')}
              >
                <Icon size={14} aria-hidden />
                {label}
              </button>
            ))}
          </div>
          {collapsed && (
            <button
              type="button"
              aria-label={`Theme: ${choice}. Switch to ${nextTheme(choice)}`}
              title={`Theme: ${choice}`}
              onClick={() => setChoice(nextTheme(choice))}
              className="mb-2 hidden w-full justify-center rounded p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white lg:flex"
            >
              <CurrentThemeIcon size={18} aria-hidden />
            </button>
          )}

          <div className={rail}>
            <div className="truncate text-slate-300">{user?.email}</div>
            <div className="text-slate-300/60">
              {user?.is_superuser ? 'Superuser' : (user?.groups?.[0] ?? 'Admin')}
            </div>
          </div>
          <button
            type="button"
            title="Sign out"
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
            className={`mt-2 flex w-full items-center justify-center gap-2 min-h-[44px] rounded border border-slate-700 px-2 text-slate-200 hover:bg-slate-800 lg:min-h-0 lg:py-1 ${collapsed ? 'lg:border-0 lg:p-1.5' : ''}`}
          >
            <LogOut size={14} aria-hidden />
            <span className={collapsed ? 'lg:sr-only' : ''}>Sign out</span>
          </button>
        </div>
      </aside>

      {/* min-w-0: a flex child otherwise grows to its widest table, which made the
          whole content area (header, filters, pager) scroll sideways */}
      <main className="flex min-h-screen min-w-0 flex-1 flex-col overflow-auto pt-12 lg:pt-0">
        <div className="mx-auto w-full max-w-6xl flex-1 p-4 sm:p-6">
          <Outlet />
        </div>
        <footer className="mx-auto w-full max-w-6xl px-4 pb-4 sm:px-6">
          <p className="border-t border-slate-200 pt-3 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} TechSynergy Corp. All rights reserved.
            {' '}Talky™ is a TechSynergy Corp. service.
          </p>
        </footer>
      </main>
    </div>
  );
}
